<template>
  <div class="add-workers">
    <div class="header-section">
      <h2>➕ {{ $t('addWorkers.title') }}</h2>
      <button @click="closeAddWorkers" class="close-btn">✕</button>
    </div>

    <div class="shift-info">
      <h3>📅 {{ formatFullDate(shift.start_time) }}</h3>
      <p class="shift-time">{{ $t('addWorkers.shiftTime') }}: {{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }}</p>
    </div>

    <!-- Current Workers (if any) -->
    <div v-if="shift.assignments && shift.assignments.length > 0" class="current-workers-section">
      <h4>{{ $t('shiftCalendar.assignedWorkers') }}:</h4>
      <div class="current-workers-list">
        <span 
          v-for="assignment in shift.assignments" 
          :key="assignment.id"
          class="current-worker-tag"
        >
          {{ assignment.worker.first_name }} {{ assignment.worker.last_name }}
        </span>
      </div>
    </div>

    <!-- Available Workers -->
    <div class="available-workers-section">
      <h4>{{ $t('addWorkers.selectWorkers') }}:</h4>
      
      <div v-if="loadingWorkers" class="loading-state">
        <div class="loading-spinner"></div>
        <span>{{ $t('common.loading') }}</span>
      </div>
      
      <div v-else-if="availableWorkers.length === 0" class="no-available">
        <p>{{ $t('addWorkers.alreadyAssigned') }}</p>
      </div>
      
      <div v-else class="worker-selection">
        <div 
          v-for="worker in availableWorkers" 
          :key="worker.id"
          class="worker-item"
          :class="{ 'selected': selectedWorkerIds.includes(worker.id) }"
          @click="toggleWorkerSelection(worker.id)"
        >
          <div class="worker-info">
            <span class="worker-name">{{ worker.first_name }} {{ worker.last_name }}</span>
            <span class="worker-role">{{ worker.role }}</span>
          </div>
          <div class="selection-indicator">
            <span v-if="selectedWorkerIds.includes(worker.id)">✓</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="form-actions">
      <button @click="closeAddWorkers" class="cancel-btn">
        {{ $t('common.cancel') }}
      </button>
      <button 
        v-if="selectedWorkerIds.length > 0"
        @click="addSelectedWorkers" 
        :disabled="saving" 
        class="add-btn"
      >
        {{ saving ? $t('common.loading') : `${$t('addWorkers.addSelected')} (${selectedWorkerIds.length})` }}
      </button>
    </div>

    <!-- Messages -->
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { useShiftStore } from '../stores/shift.js'
import { shiftApi } from '../services/shiftApi.js'



const props = defineProps({
  shift: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['workers-added', 'close'])

// Reactive data
const authStore = useAuthStore()
const shiftStore = useShiftStore()
const saving = ref(false)
const loadingWorkers = ref(false)
const message = ref('')
const messageType = ref('success')

const allWorkers = ref([])
const selectedWorkerIds = ref([])

// Computed
const availableWorkers = computed(() => {
  const assignedWorkerIds = (props.shift.assignments || []).map(a => a.worker.id)
  return allWorkers.value.filter(worker => !assignedWorkerIds.includes(worker.id))
})

// Methods
onMounted(async () => {
  await loadAllWorkers()
})

async function loadAllWorkers() {
  try {
    loadingWorkers.value = true
    
    if (!authStore.isAuthenticated) {
      showMessage('Not authenticated', 'error')
      return
    }
    
  const response = await authStore.makeAuthenticatedRequest('/api/users/')
    
    if (response.ok) {
      allWorkers.value = await response.json()
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
  } catch (error) {
    console.error('Error loading workers:', error)
    showMessage('Failed to load workers: ' + error.message, 'error')
  } finally {
    loadingWorkers.value = false
  }
}

function toggleWorkerSelection(workerId) {
  const index = selectedWorkerIds.value.indexOf(workerId)
  if (index > -1) {
    selectedWorkerIds.value.splice(index, 1)
  } else {
    selectedWorkerIds.value.push(workerId)
  }
}

async function addSelectedWorkers() {
  if (selectedWorkerIds.value.length === 0) return
  
  try {
    saving.value = true
    
    // Calculate status based on 14-day rule
    const shiftStartTime = new Date(props.shift.start_time)
    const now = new Date()
    const daysUntilShift = (shiftStartTime - now) / (1000 * 60 * 60 * 24)
    const status = daysUntilShift <= 14 ? 'pending' : 'approved'
    
    // Create new assignments with appropriate status
    const assignmentPromises = selectedWorkerIds.value.map(workerId => 
      shiftApi.assignWorkerToShift(props.shift.id, workerId, status)
    )
    
    await Promise.all(assignmentPromises)
    
    showMessage(`Added ${selectedWorkerIds.value.length} worker(s) to shift`, 'success')
    
    // Notify store to trigger reactivity in other components
    shiftStore.notifyShiftsChanged()
    
    // Emit success event
    emit('workers-added')
    
  } catch (error) {
    console.error('Error adding workers:', error)
    showMessage('Failed to add workers: ' + error.message, 'error')
  } finally {
    saving.value = false
  }
}

function closeAddWorkers() {
  emit('close')
}

function formatTime(dateTimeString) {
  return new Date(dateTimeString).toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatFullDate(dateTimeString) {
  return new Date(dateTimeString).toLocaleDateString('sv-SE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

function showMessage(msg, type) {
  message.value = msg
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 5000)
}
</script>

<style scoped>
.add-workers {
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
}

.header-section h2 {
  color: #333;
  margin: 0;
}

.close-btn {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.05);
}

.shift-info {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
}

.shift-info h3 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
}

.shift-time {
  font-size: 1.1rem;
  font-weight: 600;
  color: #667eea;
  margin: 0;
}

.current-workers-section {
  margin-bottom: 2rem;
}

.current-workers-section h4 {
  margin: 0 0 1rem 0;
  color: #374151;
}

.current-workers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.current-worker-tag {
  background: #e5e7eb;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
}

.available-workers-section h4 {
  margin: 0 0 1rem 0;
  color: #374151;
}

.worker-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.worker-item:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.worker-item.selected {
  background: #eff6ff;
  border-color: #3b82f6;
}

.worker-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.worker-name {
  font-weight: 600;
  color: #374151;
}

.worker-role {
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: capitalize;
}

.selection-indicator {
  width: 24px;
  text-align: center;
  font-weight: bold;
  color: #3b82f6;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
}

.cancel-btn, .add-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.cancel-btn:hover {
  background: #e2e8f0;
}

.add-btn {
  background: #10b981;
  color: white;
  border: none;
}

.add-btn:hover {
  background: #059669;
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.message {
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-weight: 500;
}

.message.success {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.message.error {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.loading-state, .no-available {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-style: italic;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>